import mapboxgl, { CameraOptions } from "mapbox-gl";
import React, {
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useResizeDetector } from "react-resize-detector";
import {
	EventCategory,
	LocatedVelavuDevice,
	LocationType,
	MeasurementSystem,
	VelavuDevice,
	VelavuEvent,
	VelavuSite,
} from "velavu-js-api";
import { mapboxStyle, mapboxStyleSatellite } from "../../constants";
import AnalyticsStatType, {
	getNumericDeviceStat,
} from "../../data/analytics-stat-type";
import { DateRange } from "../../data/date-range";
import DeviceMarker, {
	deviceToDeviceMarker,
	resolveSiteBoundaryViewport,
} from "../../data/device-marker";
import MapStyle from "../../data/map-style";
import IconArrowRange from "../../dynamicicons/icon-arrow-range";
import IconCalendar from "../../dynamicicons/icon-calendar";
import DateRangeSelector from "../../elements/date-range-selector";
import FlatButton from "../../elements/flat-button";
import VelavuSwitch from "../../elements/velavu-switch";
import {
	getFloorTarget,
	getLocationFloorID,
	isSiteFloorFilterable,
} from "../../helper/floor-helper";
import { interpolateWeightedLine } from "../../helper/geo-helper";
import { useElementSize } from "../../helper/hook-helper";
import { makeStyleable } from "../../helper/icon-helper";
import { formatDateRange, formatUnit } from "../../helper/language-helper";
import {
	addImagesToMapboxStyle,
	useListenerSetupMapEffect,
	useMapReadyEmitter,
} from "../../helper/map-helper";
import { clamp, doBoundsIntersect } from "../../helper/math-helper";
import { classArr } from "../../helper/style-helper";
import useMapboxInjectableDevices from "../../injectable/mapbox/mapbox-injectable-devices";
import useMapboxInjectableHotspot, {
	HotspotPoint,
} from "../../injectable/mapbox/mapbox-injectable-hotspot";
import useMapboxInjectableLocationHistory, {
	InterpolatedLocationHistoryEntry,
} from "../../injectable/mapbox/mapbox-injectable-location-history";
import useMapboxInjectableMeasureDistance, {
	isMeasureDistanceLinePlacing,
	MeasureDistanceLine,
} from "../../injectable/mapbox/mapbox-injectable-measure-distance";
import useMapboxInjectableSiteBoundaries from "../../injectable/mapbox/mapbox-injectable-site-boundaries";
import useMapboxInjectableSites from "../../injectable/mapbox/mapbox-injectable-sites";
import useMapboxInjectableSiteIndicators from "../../injectable/mapbox/mapbox-injectable-site-indicators";
import MapContextMenu, {
	MapContextMenuItem,
} from "../map-context-menu/map-context-menu";
import { useSignInContext } from "../sign-in-context/sign-in-context";
import whitelabel from "../../whitelabel";
import FloorSelector from "../../widgets/floor-selector";
import MapControls from "../../widgets/map-controls";
import { mapAnalyticsUnitRange, UnitRange } from "./analytics";
import AnalyticsCardHeader from "./analytics-card-header";
import * as styles from "./analytics-map.module.scss";
import AnalyticsTimeSlider from "./analytics-time-slider";

const MAP_CAMERA_OPTIONS: CameraOptions = {
	maxZoom: 20,
	padding: 50,
};
const MAP_ZOOM_LEVEL_DELTA = 3;

export default function AnalyticsMap(props: {
	sites: VelavuSite[];
	stat: AnalyticsStatType;
	measurementSystem: MeasurementSystem;

	devices: LocatedVelavuDevice[];
	lastSelectedDevice?: VelavuDevice;

	dateRange: DateRange;
	setDateRange: (range: DateRange) => void;
	locationData: {
		[key: string]: VelavuEvent<EventCategory.Location>[];
	};
}) {
	const { t } = useTranslation();
	const userContext = useSignInContext();
	const mapRef = useRef<HTMLDivElement | null>(null);
	const [mapboxMap, setMapboxMap] = useState<mapboxgl.Map | undefined>(
		undefined,
	);
	const mapInstance = useMapReadyEmitter(mapboxMap, async (map) => {
		await addImagesToMapboxStyle(map);
	});

	const [mapStyle, setMapStyle] = useState(MapStyle.Graphic);
	const toggleMapStyle = useCallback(() => {
		setMapStyle((current) =>
			current === MapStyle.Graphic
				? MapStyle.Satellite
				: MapStyle.Graphic,
		);
	}, []);

	//Floor overrides keyed by site ID, used to drive both the
	//floorplan rendering and the floor selector widget
	const [sitesFloorOverride, setSitesFloorOverride] = useState<{
		[key: string]: string | undefined;
	}>({});

	//When the user selects a device that is inside a site, switch the
	//floor override for that site to the device's floor. If multiple
	//devices are selected, the most recently selected one wins.
	const { lastSelectedDevice } = props;
	useEffect(() => {
		if (lastSelectedDevice === undefined) return;

		const siteID = lastSelectedDevice.site_id;
		const locationFloorID = getLocationFloorID(lastSelectedDevice.location);
		if (siteID === undefined || locationFloorID === undefined) return;

		setSitesFloorOverride((overrides) => {
			if (overrides[siteID] === locationFloorID) return overrides;
			return { ...overrides, [siteID]: locationFloorID };
		});
	}, [lastSelectedDevice]);

	const updateSiteFloorSelection = useCallback(
		(siteID: string, floorID: string | undefined) => {
			setSitesFloorOverride((overrides) => ({
				...overrides,
				[siteID]: floorID,
			}));
		},
		[],
	);

	const mapContainerDimensions = useElementSize(mapRef);

	const { stat: propsStat } = props;

	//Reset the slider time when the date range changes
	const [sliderTime, setSliderTime] = useState<Date>(props.dateRange.end);
	useEffect(() => {
		setSliderTime(props.dateRange.end);
	}, [setSliderTime, props.dateRange]);

	const [showPoints, setShowPoints] = useState(false);
	const [isPickerOpen, setIsPickerOpen] = useState(false);
	const [clickMenu, setClickMenu] = useState<React.ReactNode>();
	const [measureLine, setMeasureLine] = useState<MeasureDistanceLine | null>(
		null,
	);
	const isPlacingMeasureLine = isMeasureDistanceLinePlacing(measureLine);

	const togglePickerOpen = useCallback(
		() => setIsPickerOpen((open) => !open),
		[],
	);

	const { setDateRange: propsSetDateRange } = props;
	const handleApplyDateRange = useCallback(
		(range: DateRange) => {
			propsSetDateRange(range);
			setIsPickerOpen(false);
		},
		[propsSetDateRange],
	);

	useEffect(() => {
		//Wait until Mapbox is ready
		if (mapboxMap !== undefined || !userContext.isMapboxRegistered) return;

		//Find the center of all sites
		const centerPosition: [number, number] = [0, 0];
		if (props.sites.length > 0) {
			for (const site of props.sites) {
				const coordinates = site.coordinates;

				centerPosition[0] += coordinates[0];
				centerPosition[1] += coordinates[1];
			}

			centerPosition[0] /= props.sites.length;
			centerPosition[1] /= props.sites.length;
		}

		//Create the map
		const map = new mapboxgl.Map({
			container: mapRef.current!,
			style: mapboxStyle,
			center: centerPosition,
			zoom: 2,
			language: "auto",
		});
		setMapboxMap(map);
	}, [mapboxMap, props.sites, userContext.isMapboxRegistered, setMapboxMap]);

	//Apply map style changes
	const initialMapStyleSet = useRef(false);
	const mapInstanceInvalidate = mapInstance.invalidate;
	useEffect(() => {
		if (mapboxMap === undefined) return;

		//Don't set the map style on the first round
		if (!initialMapStyleSet.current) {
			initialMapStyleSet.current = true;
			return;
		}

		let mapStyleValue: string;
		switch (mapStyle) {
			case MapStyle.Graphic:
				mapStyleValue = mapboxStyle;
				break;
			case MapStyle.Satellite:
				mapStyleValue = mapboxStyleSatellite;
				break;
		}
		mapInstanceInvalidate();
		mapboxMap.setStyle(mapStyleValue);
	}, [mapboxMap, mapStyle, mapInstanceInvalidate]);

	//Subscribe to resize updates
	const {
		width: mapWidth,
		height: mapHeight,
		ref: mapResizeRef,
	} = useResizeDetector();
	useEffect(() => {
		mapboxMap?.resize();
	}, [mapboxMap, mapWidth, mapHeight]);

	//Track the site within the camera bounds
	const [selectedSiteID, setSelectedSiteID] = useState<string | undefined>();
	const selectedSiteInstance = useMemo(() => {
		return props.sites.find((site) => site.id === selectedSiteID);
	}, [props.sites, selectedSiteID]);
	useListenerSetupMapEffect(
		mapInstance.map,
		(map, box) => {
			const updateSelectedSite = () => {
				const mapBounds = map.getBounds();
				if (mapBounds === null) {
					setSelectedSiteID(undefined);
					return;
				}

				const mapZoom = map.getZoom();
				const mapBoundsArray: [number, number, number, number] = [
					mapBounds.getWest(),
					mapBounds.getSouth(),
					mapBounds.getEast(),
					mapBounds.getNorth(),
				];

				const targetSite = props.sites.find((site) => {
					const siteViewport = resolveSiteBoundaryViewport(
						site,
						mapContainerDimensions,
					);
					if (siteViewport === undefined) {
						return false;
					}

					if (mapZoom < siteViewport.zoom) {
						return false;
					}

					return doBoundsIntersect(
						mapBoundsArray,
						siteViewport.bounds,
					);
				});

				setSelectedSiteID(targetSite?.id);
			};

			updateSelectedSite();
			box.on("move", updateSelectedSite);
		},
		[props.sites, mapContainerDimensions],
	);

	const unitRange = mapAnalyticsUnitRange(
		props.stat,
		props.measurementSystem,
	);
	const statColorRange = mapStatColors(props.stat);

	const statTitle = useMemo(() => {
		switch (props.stat) {
			case AnalyticsStatType.Temperature:
				return t("analytics.temperature_heatmap");
			case AnalyticsStatType.Humidity:
				return t("analytics.humidity_heatmap");
			case AnalyticsStatType.Battery:
				return t("analytics.battery_heatmap");
			case AnalyticsStatType.Location:
				return t("analytics.location_history");
		}
	}, [props.stat, t]);

	const interpolatedLocationHistoryEntries =
		useMemo((): InterpolatedLocationHistoryEntry[] => {
			return Object.entries(props.locationData)
				.map(
					([deviceID, resourceArray]):
						InterpolatedLocationHistoryEntry | undefined => {
						//Find the device
						const device = props.devices.find(
							(device) => device.id === deviceID,
						);
						if (device === undefined) return undefined;

						if (propsStat !== AnalyticsStatType.Location)
							return undefined;

						//Skip empty entries
						if (resourceArray.length === 0) return undefined;

						//Skip entries without a device
						if (device === undefined) return undefined;

						//Sort the resources in ascending order
						const sortedResourceArray = [...resourceArray].sort(
							(a, b) =>
								new Date(a.timestamp).getTime() -
								new Date(b.timestamp).getTime(),
						);

						const interpolationResult = interpolateWeightedLine(
							sliderTime.getTime(),
							sortedResourceArray.map((resource) => ({
								coordinates: resource.data.coordinates,
								value: new Date(resource.timestamp).getTime(),
								extra: resource.id,
							})),
						);

						return {
							device: device,
							locationArray: sortedResourceArray,
							interpolationResult: interpolationResult,
						};
					},
				)
				.filter((it) => it !== undefined);
		}, [props.locationData, sliderTime, props.devices, propsStat]);

	const mapHotspots = useMemo(
		() =>
			props.devices
				.map((device): HotspotPoint | undefined => {
					const location = device.location.coordinates;
					const strength = inverseLerpUnit(
						getNumericDeviceStat(props.stat, device),
						unitRange,
					);

					if (strength === undefined) return undefined;
					return { location, strength };
				})
				.filter((it) => it !== undefined) as HotspotPoint[],
		[props.devices, props.stat, unitRange],
	);

	/* Get the final display objects to render to the map.
	 * When the user is viewing location history, devices can be moved
	 * to represent their location at the inspected time.
	 * For other statistic types, their latest location is used.
	 */
	const displayDeviceList = useMemo((): LocatedVelavuDevice[] => {
		if (props.stat === AnalyticsStatType.Location) {
			return interpolatedLocationHistoryEntries.map((entry) =>
				applyDeviceInternalLocation(
					entry.device,
					entry.interpolationResult.coordinates,
				),
			);
		} else {
			return props.devices;
		}
	}, [props.stat, props.devices, interpolatedLocationHistoryEntries]);

	const fitToMap = useCallback(() => {
		if (mapboxMap === undefined || displayDeviceList.length === 0) return;

		const bounds = new mapboxgl.LngLatBounds();
		displayDeviceList.forEach((device) =>
			bounds.extend(device.location.coordinates),
		);
		mapboxMap.fitBounds(bounds, MAP_CAMERA_OPTIONS);
	}, [mapboxMap, displayDeviceList]);

	const zoomInMap = useCallback(() => {
		if (mapboxMap === undefined) return;
		mapboxMap.zoomTo(mapboxMap.getZoom() + 1);
	}, [mapboxMap]);

	const zoomOutMap = useCallback(() => {
		if (mapboxMap === undefined) return;
		mapboxMap.zoomTo(mapboxMap.getZoom() - 1);
	}, [mapboxMap]);

	//Auto-fit the map to the displayed devices
	useEffect(() => {
		if (mapboxMap === undefined || displayDeviceList.length === 0) return;

		//Compute the bounds the map would fit to
		const bounds = new mapboxgl.LngLatBounds();
		displayDeviceList.forEach((device) =>
			bounds.extend(device.location.coordinates),
		);

		//Get the current bounds and zoom level of the map
		const currentBounds = mapboxMap.getBounds();
		if (currentBounds === null) return;
		const currentZoom = mapboxMap.getZoom();

		//Check if any device is outside visible bounds
		const hasOutOfViewMarker = displayDeviceList.some(
			(device) => !currentBounds.contains(device.location.coordinates),
		);

		//Determine the zoom level that fitBounds would use
		const cameraOptions = mapboxMap.cameraForBounds(
			bounds,
			MAP_CAMERA_OPTIONS,
		);
		if (!cameraOptions) {
			return;
		}
		const targetZoom = cameraOptions.zoom;

		//Reposition only a device is out of view, or we are zoomed out further than is appropriate
		const zoomDifferenceTooHigh =
			targetZoom === undefined ||
			targetZoom - currentZoom > MAP_ZOOM_LEVEL_DELTA;
		if (!hasOutOfViewMarker && !zoomDifferenceTooHigh) {
			return;
		}

		mapboxMap.fitBounds(bounds, MAP_CAMERA_OPTIONS);
	}, [displayDeviceList, mapboxMap]);

	useMapboxInjectableHotspot(mapInstance, mapHotspots, statColorRange ?? []);

	const mapboxLayerIDSiteIndicators = useMapboxInjectableSiteIndicators(
		mapInstance,
		{
			sites: props.sites,
			mapDimensions: mapContainerDimensions,
			disableInteraction: isPlacingMeasureLine,
		},
	);

	const displayDeviceMarkerList = useMemo((): DeviceMarker[] => {
		return displayDeviceList.map((device) =>
			deviceToDeviceMarker(device, mapContainerDimensions),
		);
	}, [displayDeviceList, mapContainerDimensions]);
	const mapboxLayerIDDevices = useMapboxInjectableDevices({
		mapInstance: mapInstance,
		devicesSource: displayDeviceMarkerList,
		belowLayer: mapboxLayerIDSiteIndicators,
		disableInteraction: isPlacingMeasureLine,
	});
	const mapboxLayerIDSiteBoundaries = useMapboxInjectableSiteBoundaries(
		mapInstance,
		{
			sites: props.sites,
			belowLayer: mapboxLayerIDDevices,
		},
	);
	const highlightedFloorplanSiteIDs = useMemo(() => {
		return selectedSiteID === undefined ? [] : [selectedSiteID];
	}, [selectedSiteID]);
	useMapboxInjectableSites(mapInstance, {
		sites: props.sites,
		floorOverride: sitesFloorOverride,
		highlightedSiteIDs: highlightedFloorplanSiteIDs,
		belowLayer: mapboxLayerIDSiteBoundaries,
	});
	useMapboxInjectableLocationHistory(
		mapInstance,
		interpolatedLocationHistoryEntries,
		showPoints,
		isPlacingMeasureLine,
	);
	useMapboxInjectableMeasureDistance({
		mapInstance: mapInstance,
		line: measureLine,
		onChangeLine: setMeasureLine,
		measurementSystem: props.measurementSystem,
	});

	useListenerSetupMapEffect(
		mapInstance.map,
		(map, box) => {
			box.on("contextmenu", (e) => {
				const items: MapContextMenuItem[] = [
					{
						icon: makeStyleable(IconArrowRange),
						label: t("shared.map_context_menu.measure_distance"),
						onClick: () => {
							setClickMenu(undefined);

							//Use timeout to avoid the click passing through to the map
							setTimeout(() => {
								setMeasureLine({
									start: [e.lngLat.lng, e.lngLat.lat],
									end: null,
								});
							});
						},
					},
				];

				setClickMenu(
					<MapContextMenu
						key={`${e.lngLat.lng}${e.lngLat.lat}`}
						map={map}
						lngLat={e.lngLat}
						items={items}
					/>,
				);
			});

			box.on("click", () => {
				setClickMenu(undefined);
			});
		},
		[t],
	);

	const floorSelectorSite = useMemo(() => {
		const siteID = lastSelectedDevice?.site_id;
		if (siteID === undefined) {
			return undefined;
		}
		const site = props.sites.find((site) => site.id === siteID);
		if (site === undefined) {
			return undefined;
		}
		return site;
	}, [lastSelectedDevice, props.sites]);

	const handleSelectFloor = useCallback(
		(floorID: string | undefined) => {
			if (floorSelectorSite === undefined) return;
			updateSiteFloorSelection(floorSelectorSite.id, floorID);
		},
		[floorSelectorSite, updateSiteFloorSelection],
	);

	return (
		<>
			<AnalyticsCardHeader title={statTitle}>
				{unitRange !== undefined && (
					<div className={styles.heatmapBar}>
						<span className={styles.heatmapBarLabel}>
							{formatUnit(props.stat, unitRange.min)}
						</span>
						<span
							style={{
								background: `linear-gradient(to right, ${statColorRange?.join(
									", ",
								)})`,
							}}
							className={styles.heatmapBarSlider}
						/>
						<span className={styles.heatmapBarLabel}>
							{formatUnit(props.stat, unitRange.max)}
						</span>
					</div>
				)}

				{props.stat === AnalyticsStatType.Location && (
					<>
						<VelavuSwitch
							label={t("analytics.show_points")}
							toggled={showPoints}
							onChange={setShowPoints}
						/>

						<div
							className={classArr(
								styles.rangePickerWrapper,
								isPickerOpen && styles.rangePickerWrapperOpen,
							)}
						>
							<FlatButton
								className={styles.rangePickerButton}
								icon={makeStyleable(IconCalendar)}
								label={formatDateRange(props.dateRange)}
								onClick={togglePickerOpen}
							/>

							<div className={styles.rangePickerPopover}>
								<DateRangeSelector
									range={props.dateRange}
									onApply={handleApplyDateRange}
								/>
							</div>
						</div>
					</>
				)}
			</AnalyticsCardHeader>

			{props.stat === AnalyticsStatType.Location && (
				<AnalyticsTimeSlider
					range={props.dateRange}
					value={sliderTime}
					onChange={setSliderTime}
				/>
			)}

			<div ref={mapResizeRef} className={styles.mapContainer}>
				<div ref={mapRef} className={styles.map} />
				{clickMenu}

				{floorSelectorSite !== undefined && (
					<FloorSelector
						className={styles.floorSelector}
						site={floorSelectorSite}
						onSelectFloor={handleSelectFloor}
						selectedFloor={getFloorTarget(
							floorSelectorSite.floors,
							sitesFloorOverride[floorSelectorSite.id],
						)}
					/>
				)}

				<MapControls
					className={styles.mapControls}
					labelFit={t("dashboard.map_controls.show_all_assets")}
					onFit={fitToMap}
					labelMapStyle={
						mapStyle === MapStyle.Graphic
							? t("dashboard.map_controls.satellite")
							: t("dashboard.map_controls.graphic")
					}
					onChangeMapStyle={toggleMapStyle}
					onZoomIn={zoomInMap}
					onZoomOut={zoomOutMap}
				/>
			</div>
		</>
	);
}

function mapStatColors(stat: AnalyticsStatType): string[] | undefined {
	switch (stat) {
		case AnalyticsStatType.Temperature:
			return [
				whitelabel.palette.primary900,
				whitelabel.palette.warning500,
				whitelabel.palette.error500,
			];
		case AnalyticsStatType.Humidity:
			return [
				whitelabel.palette.error500,
				whitelabel.palette.warning500,
				whitelabel.palette.primary900,
			];
		case AnalyticsStatType.Battery:
			return [
				whitelabel.palette.error500,
				whitelabel.palette.warning500,
				whitelabel.palette.active500,
			];
		default:
			return undefined;
	}
}

function applyDeviceInternalLocation(
	device: VelavuDevice,
	coordinates: [number, number],
): LocatedVelavuDevice {
	return {
		...device,
		location: {
			location_type: LocationType.Internal,
			timestamp: new Date().toISOString(),
			coordinates: coordinates,
		},
	};
}

function inverseLerpUnit(
	value: number | undefined,
	range: UnitRange | undefined,
) {
	if (value === undefined || range === undefined) return undefined;
	return clamp((value - range.min) / (range.max - range.min), 0, 1);
}
